import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertTriangle,
  ArrowLeft,
  GripVertical,
  MapPin,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { MapLocationPicker, type PickedLocation } from "@/shared/components/MapLocationPicker";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";
import { randomId } from "@/shared/utils/randomId";
import { useActiveTransportRates } from "../hooks/useTransportRates";
import {
  useCreateRouteTemplate,
  useDeleteRouteTemplate,
  useRouteTemplate,
  useRouteTemplateList,
  useUpdateRouteTemplate,
} from "../hooks/useRouteTemplates";
import type { TransportRateDto } from "../services/transportRatesApi";
import type {
  RoutePointType,
  RouteTemplateDto,
  RouteTemplateSummaryDto,
  StopOrderPolicy,
} from "../services/routeTemplatesApi";

type PageMode = { type: "list" } | { type: "create" } | { type: "edit"; id: string };

const DEFAULT_DEPARTURE_NAME = "0°44′03″S 37°19′32″E";
const DEFAULT_DEPARTURE_LOCATION: PickedLocation = {
  latitude: -(44 / 60 + 3 / 3600),
  longitude: 37 + 19 / 60 + 32 / 3600,
};

type StopForm = {
  localId: string;
  id?: string;
  transportRateId: string;
  stopName: string;
  pointType: RoutePointType;
  address: string;
  town: string;
  estimatedArrivalOffsetMinutes: string;
  isCollectionPoint: boolean;
  isActive: boolean;
  notes: string;
};

const emptyStop = (): StopForm => ({
  localId: randomId(),
  transportRateId: "",
  stopName: "",
  pointType: "MainStop",
  address: "",
  town: "",
  estimatedArrivalOffsetMinutes: "",
  isCollectionPoint: false,
  isActive: true,
  notes: "",
});

const policyLabel = (policy: StopOrderPolicy) =>
  policy === "AllowOutOfOrder" ? "Flexible" : "Strict";

const parseOptionalNumber = (value: string) => {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const routeMarkerIcon = (label: string, color: string) =>
  L.divIcon({
    className: "route-preview-marker",
    html: `<span style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:9999px;background:${color};color:white;font-size:12px;font-weight:700;border:2px solid white;box-shadow:0 2px 8px rgba(15,23,42,.35);">${label}</span>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });

type PreviewPoint = {
  label: string;
  position: [number, number];
  title: string;
  price?: number;
};

const FitRouteBounds = ({ points }: { points: PreviewPoint[] }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    const bounds = L.latLngBounds(points.map((point) => point.position));
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 13 });
  }, [map, points]);
  return null;
};

const RouteMapPreview = ({ points }: { points: PreviewPoint[] }) => {
  if (points.length === 0) {
    return (
      <div className="flex h-80 items-center justify-center rounded-md border bg-muted/40 text-sm text-muted-foreground">
        Add mapped route points to preview the route.
      </div>
    );
  }

  const center = points[0].position;
  const line = points.map((point) => point.position);

  return (
    <div className="h-80 overflow-hidden rounded-md border">
      <MapContainer center={center} zoom={8} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitRouteBounds points={points} />
        {line.length > 1 && <Polyline positions={line} pathOptions={{ color: "#2563eb", weight: 4 }} />}
        {points.map((point, index) => (
          <Marker
            key={`${point.label}-${index}`}
            position={point.position}
            icon={routeMarkerIcon(point.label, index === 0 ? "#16a34a" : index === points.length - 1 ? "#dc2626" : "#2563eb")}
          >
            <Popup>
              <div className="space-y-1">
                <p className="font-semibold">{point.title}</p>
                {point.price != null && <p>Delivery price: KES {point.price.toLocaleString()}</p>}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

const SortableStop = ({
  stop,
  index,
  mappedRates,
  onChange,
  onRemove,
  unavailableRateIds,
}: {
  stop: StopForm;
  index: number;
  mappedRates: TransportRateDto[];
  onChange: (patch: Partial<StopForm>) => void;
  onRemove: () => void;
  unavailableRateIds: Set<string>;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stop.localId,
  });
  const selectedRate = mappedRates.find((rate) => rate.id === stop.transportRateId);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`grid gap-3 rounded-md border bg-background p-3 lg:grid-cols-[auto_1.2fr_.8fr_.8fr_.8fr_auto] ${
        isDragging ? "shadow-md" : ""
      }`}
    >
      <button
        type="button"
        className="mt-1 flex h-9 w-9 items-center justify-center rounded-md border text-muted-foreground"
        aria-label={`Reorder stop ${index + 1}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <Field>
        <FieldLabel>Stop {index + 1}</FieldLabel>
        <Select
          value={stop.transportRateId || "none"}
          onValueChange={(value) => {
            const rate = mappedRates.find((r) => r.id === value);
            onChange({
              transportRateId: value === "none" ? "" : value,
              stopName: rate?.destinationName ?? "",
              town: rate?.destinationName ?? "",
            });
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Optional pricing destination" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No pricing destination</SelectItem>
            {mappedRates.map((rate) => (
              <SelectItem
                key={rate.id}
                value={rate.id}
                disabled={rate.id !== stop.transportRateId && unavailableRateIds.has(rate.id)}
              >
                {rate.destinationName}
                {rate.id !== stop.transportRateId && unavailableRateIds.has(rate.id) ? " (already used)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          className="mt-2"
          value={stop.stopName}
          onChange={(event) => onChange({ stopName: event.target.value })}
          placeholder="Stop name"
          aria-label={`Stop ${index + 1} name`}
        />
      </Field>

      <Field>
        <FieldLabel>Point type</FieldLabel>
        <Select
          value={stop.pointType}
          onValueChange={(value) =>
            onChange({
              pointType: value as RoutePointType,
              isCollectionPoint: value === "CollectionPoint" ? true : stop.isCollectionPoint,
            })
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="MainStop">Main stop</SelectItem>
            <SelectItem value="CollectionPoint">Collection point</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field>
        <FieldLabel>Town</FieldLabel>
        <Input value={stop.town} onChange={(e) => onChange({ town: e.target.value })} />
      </Field>

      <Field>
        <FieldLabel>Arrival offset</FieldLabel>
        <Input
          type="number"
          min="0"
          step="1"
          value={stop.estimatedArrivalOffsetMinutes}
          onChange={(e) => onChange({ estimatedArrivalOffsetMinutes: e.target.value })}
          placeholder="Minutes"
        />
      </Field>

      <Field>
        <FieldLabel>Notes</FieldLabel>
        <Input
          value={stop.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Optional stop note"
        />
      </Field>

      <Field className="lg:col-span-4 lg:col-start-2">
        <FieldLabel>Address</FieldLabel>
        <Input
          value={stop.address}
          onChange={(e) => onChange({ address: e.target.value })}
          placeholder="Optional pickup/drop-off details"
        />
      </Field>

      <div className="flex flex-wrap items-center gap-4 lg:col-span-4 lg:col-start-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`collection-${stop.localId}`}
            checked={stop.isCollectionPoint}
            onCheckedChange={(value) => onChange({ isCollectionPoint: value === true })}
          />
          <Label htmlFor={`collection-${stop.localId}`}>Collection point</Label>
        </div>
        {selectedRate && (
          <Badge variant="outline">
            KES {selectedRate.costAmount.toLocaleString()} · {selectedRate.region || "No region"}
          </Badge>
        )}
        {!stop.isActive && <Badge variant="secondary">Inactive stop</Badge>}
      </div>

      <Button type="button" variant="ghost" size="icon" className="mt-6" onClick={onRemove}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

const RouteTemplateForm = ({
  mode,
  template,
  onCancel,
  onSaved,
}: {
  mode: "create" | "edit";
  template?: RouteTemplateDto;
  onCancel: () => void;
  onSaved: () => void;
}) => {
  const { data: rates = [] } = useActiveTransportRates();
  const mappedRates = rates;
  const createMutation = useCreateRouteTemplate();
  const updateMutation = useUpdateRouteTemplate();
  const confirm = useConfirm();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [departureName, setDepartureName] = useState(
    mode === "create" ? DEFAULT_DEPARTURE_NAME : ""
  );
  const [departureLocation, setDepartureLocation] = useState<PickedLocation | null>(
    mode === "create" ? DEFAULT_DEPARTURE_LOCATION : null
  );
  const [finalDestinationName, setFinalDestinationName] = useState("");
  const [finalDestinationTransportRateId, setFinalDestinationTransportRateId] = useState("");
  const [majorTown, setMajorTown] = useState("");
  const [region, setRegion] = useState("");
  const [estimatedDistanceKm, setEstimatedDistanceKm] = useState("");
  const [estimatedDurationMinutes, setEstimatedDurationMinutes] = useState("");
  const [stopOrderPolicy, setStopOrderPolicy] = useState<StopOrderPolicy>("Strict");
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [stops, setStops] = useState<StopForm[]>([emptyStop()]);

  useEffect(() => {
    if (!template) return;
    setName(template.name);
    setCode(template.code ?? "");
    setDepartureName(template.departureName ?? "");
    setDepartureLocation(
      template.departureLatitude != null && template.departureLongitude != null
        ? { latitude: template.departureLatitude, longitude: template.departureLongitude }
        : null
    );
    setFinalDestinationName(template.finalDestinationName ?? "");
    setFinalDestinationTransportRateId(template.finalDestinationTransportRateId ?? "");
    setMajorTown(template.majorTown ?? "");
    setRegion(template.region ?? "");
    setEstimatedDistanceKm(template.estimatedDistanceKm?.toString() ?? "");
    setEstimatedDurationMinutes(template.estimatedDurationMinutes?.toString() ?? "");
    setStopOrderPolicy(template.stopOrderPolicy);
    setNotes(template.notes ?? "");
    setIsActive(template.isActive);
    setStops(
      template.stops.length > 0
        ? template.stops.map((stop) => ({
            localId: stop.id,
            id: stop.id,
            transportRateId: stop.transportRateId ?? "",
            stopName: stop.stopName,
            pointType: stop.pointType ?? "MainStop",
            address: stop.address ?? "",
            town: stop.town ?? "",
            estimatedArrivalOffsetMinutes: stop.estimatedArrivalOffsetMinutes?.toString() ?? "",
            isCollectionPoint: stop.isCollectionPoint,
            isActive: stop.isActive,
            notes: stop.notes ?? "",
          }))
        : [emptyStop()]
    );
  }, [template]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setStops((items) => {
      const oldIndex = items.findIndex((item) => item.localId === active.id);
      const newIndex = items.findIndex((item) => item.localId === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedStops = stops
      .filter((stop) => stop.stopName.trim())
      .map((stop, index) => ({
        id: stop.id,
        transportRateId: stop.transportRateId || undefined,
        stopName: stop.stopName.trim(),
        pointType: stop.pointType,
        sequence: index + 1,
        address: stop.address.trim() || undefined,
        town: stop.town.trim() || undefined,
        estimatedArrivalOffsetMinutes: parseOptionalNumber(stop.estimatedArrivalOffsetMinutes),
        isCollectionPoint: stop.isCollectionPoint || stop.pointType === "CollectionPoint",
        isActive: stop.isActive,
        notes: stop.notes.trim() || undefined,
      }));

    if (!name.trim()) {
      toast.error("Route name is required.");
      return;
    }
    if (!departureName.trim()) {
      toast.error("Departure location is required.");
      return;
    }

    const resolvedFinalDestinationTransportRateId = finalDestinationTransportRateId || undefined;

    const repeatedStopId = normalizedStops
      .map((stop) => stop.transportRateId)
      .filter((id): id is string => !!id)
      .find((id, index, ids) => ids.indexOf(id) !== index);
    if (repeatedStopId) {
      const rate = mappedRates.find((item) => item.id === repeatedStopId);
      toast.error(`${rate?.destinationName ?? "This destination"} is already used as a route stop.`);
      return;
    }
    if (resolvedFinalDestinationTransportRateId && normalizedStops.some((stop) => stop.transportRateId === resolvedFinalDestinationTransportRateId)) {
      const rate = mappedRates.find((item) => item.id === resolvedFinalDestinationTransportRateId);
      toast.error(`${rate?.destinationName ?? "The final destination"} is already used as an intermediate stop.`);
      return;
    }

    const selectedFinal = mappedRates.find((rate) => rate.id === resolvedFinalDestinationTransportRateId);
    const payload = {
      name: name.trim(),
      code: code.trim() || undefined,
      departureName: departureName.trim(),
      departureLatitude: departureLocation?.latitude,
      departureLongitude: departureLocation?.longitude,
      finalDestinationName:
        finalDestinationName.trim() || selectedFinal?.destinationName || undefined,
      finalDestinationTransportRateId: resolvedFinalDestinationTransportRateId,
      majorTown: majorTown.trim() || undefined,
      region: region.trim() || selectedFinal?.region || undefined,
      estimatedDistanceKm: parseOptionalNumber(estimatedDistanceKm),
      estimatedDurationMinutes: parseOptionalNumber(estimatedDurationMinutes),
      stopOrderPolicy,
      notes: notes.trim() || undefined,
      stops: normalizedStops,
    };

    confirm({
      title: mode === "create" ? "Create route template?" : "Save route template?",
      description:
        normalizedStops.length === 0
          ? `${payload.name} will go directly to the final destination.`
          : `${payload.name} will use ${normalizedStops.length} intermediate stop${
              normalizedStops.length === 1 ? "" : "s"
            }.`,
      confirmLabel: mode === "create" ? "Create route" : "Save route",
      onConfirm: () => {
        const action =
          mode === "create"
            ? createMutation.mutateAsync(payload)
            : updateMutation.mutateAsync({
                id: template!.id,
                data: { ...payload, id: template!.id, isActive },
              });
        return withConfirmSuccess(action, "Route template saved").then(onSaved);
      },
    });
  };

  const previewPoints = useMemo<PreviewPoint[]>(() => {
    const points: PreviewPoint[] = [];
    if (departureLocation) {
      points.push({
        label: "S",
        position: [departureLocation.latitude, departureLocation.longitude],
        title: departureName || "Starting point",
      });
    }

    stops.forEach((stop, index) => {
      const rate = mappedRates.find((item) => item.id === stop.transportRateId);
      if (!rate || rate.destinationLatitude == null || rate.destinationLongitude == null) return;
      points.push({
        label: `${index + 1}`,
        position: [rate.destinationLatitude, rate.destinationLongitude],
        title: stop.stopName || rate.destinationName,
        price: rate.costAmount,
      });
    });

    const finalRate = mappedRates.find((item) => item.id === finalDestinationTransportRateId);
    if (finalRate?.destinationLatitude != null && finalRate.destinationLongitude != null) {
      points.push({
        label: "F",
        position: [finalRate.destinationLatitude, finalRate.destinationLongitude],
        title: finalDestinationName || finalRate.destinationName,
        price: finalRate.costAmount,
      });
    }

    return points;
  }, [departureLocation, departureName, finalDestinationName, finalDestinationTransportRateId, mappedRates, stops]);

  const selectedStopRateIds = stops
    .map((stop) => stop.transportRateId)
    .filter((id): id is string => Boolean(id));
  const unavailableRateIds = new Set<string>([
    ...selectedStopRateIds,
    ...(finalDestinationTransportRateId ? [finalDestinationTransportRateId] : []),
  ]);
  const duplicateStopRateId = selectedStopRateIds.find(
    (id, index, ids) => ids.indexOf(id) !== index
  );
  const duplicateFinalRateId = finalDestinationTransportRateId && selectedStopRateIds.includes(finalDestinationTransportRateId)
    ? finalDestinationTransportRateId
    : "";
  const duplicateRate = mappedRates.find((rate) => rate.id === (duplicateFinalRateId || duplicateStopRateId));
  const duplicateDestinationMessage = duplicateRate
    ? `${duplicateRate.destinationName} is selected more than once. Use it either as an intermediate stop or as the final destination, not both.`
    : "";

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="sticky top-0 z-10 -mx-6 flex flex-wrap items-center justify-between gap-3 border-b bg-background/95 px-6 py-3 backdrop-blur">
        <Button type="button" variant="outline" onClick={onCancel}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
          {mode === "create" ? "Create Route" : "Save Changes"}
        </Button>
      </div>

      {duplicateDestinationMessage && (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Duplicate destination</AlertTitle>
          <AlertDescription>{duplicateDestinationMessage}</AlertDescription>
        </Alert>
      )}

      <Card className="rounded-md py-0">
        <CardHeader className="border-b py-4">
          <CardTitle>Route Details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 py-4 lg:grid-cols-4">
          <Field className="lg:col-span-2">
            <FieldLabel>Route name *</FieldLabel>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field>
            <FieldLabel>Route code</FieldLabel>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. NRB-KRT" />
          </Field>
          <Field>
            <FieldLabel>Stop order policy</FieldLabel>
            <Select value={stopOrderPolicy} onValueChange={(value) => setStopOrderPolicy(value as StopOrderPolicy)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Strict">Strict</SelectItem>
                <SelectItem value="AllowOutOfOrder">Allow out of order</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              Strict enforces the exact stop sequence. Allow out of order lets dispatch confirmations happen in any stop order.
            </p>
          </Field>
          <Field>
            <FieldLabel>Major town</FieldLabel>
            <Input value={majorTown} onChange={(e) => setMajorTown(e.target.value)} placeholder="e.g. Karatina" />
          </Field>
          <Field>
            <FieldLabel>Region</FieldLabel>
            <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="e.g. Central" />
          </Field>
          <Field>
            <FieldLabel>Estimated distance (km)</FieldLabel>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={estimatedDistanceKm}
              onChange={(e) => setEstimatedDistanceKm(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel>Estimated duration (minutes)</FieldLabel>
            <Input
              type="number"
              min="0"
              step="1"
              value={estimatedDurationMinutes}
              onChange={(e) => setEstimatedDurationMinutes(e.target.value)}
            />
          </Field>
        </CardContent>
      </Card>

      <Card className="rounded-md py-0">
        <CardHeader className="border-b py-4">
          <CardTitle>Route Endpoints</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 py-4 lg:grid-cols-2">
          <Field className="lg:col-span-2">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <FieldLabel>Departure location *</FieldLabel>
              <MapLocationPicker
                title="Pick Departure Location"
                description="Search for the route start or click the map to place the departure pin."
                initial={
                  departureLocation
                    ? { latitude: departureLocation.latitude, longitude: departureLocation.longitude }
                    : null
                }
                onConfirm={(location) => {
                  setDepartureLocation(location);
                  if (!departureName.trim() && location.label) {
                    setDepartureName(location.label);
                  }
                }}
              />
              {departureLocation && (
                <Badge variant="outline" className="gap-1 text-green-700">
                  <MapPin className="h-3 w-3" />
                  {departureLocation.latitude.toFixed(4)}, {departureLocation.longitude.toFixed(4)}
                </Badge>
              )}
            </div>
            <Input
              value={departureName}
              onChange={(e) => setDepartureName(e.target.value)}
              placeholder="e.g. FarmSmart nursery gate"
              required
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Enter a recognizable route starting point, such as a nursery, warehouse, or loading bay.
            </p>
          </Field>
          <Field>
            <FieldLabel>Final pricing destination</FieldLabel>
            <Select
              value={finalDestinationTransportRateId || "none"}
              onValueChange={(value) => {
                setFinalDestinationTransportRateId(value === "none" ? "" : value);
                const rate = mappedRates.find((r) => r.id === value);
                if (rate) {
                  setFinalDestinationName(rate.destinationName);
                  if (!majorTown.trim()) setMajorTown(rate.destinationName);
                  if (!region.trim() && rate.region) setRegion(rate.region);
                }
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select mapped destination" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No fixed final destination</SelectItem>
                {mappedRates.map((rate) => (
                  <SelectItem
                    key={rate.id}
                    value={rate.id}
                    disabled={rate.id !== finalDestinationTransportRateId && selectedStopRateIds.includes(rate.id)}
                  >
                    {rate.destinationName}
                    {rate.id !== finalDestinationTransportRateId && selectedStopRateIds.includes(rate.id)
                        ? " (used as stop)"
                        : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              Optional preset. Actual delivery stops come from selected orders.
            </p>
          </Field>
          <Field>
            <FieldLabel>Final destination name</FieldLabel>
            <Input
              value={finalDestinationName}
              onChange={(e) => setFinalDestinationName(e.target.value)}
              placeholder="Defaults to selected destination"
            />
          </Field>
        </CardContent>
      </Card>

      <Card className="rounded-md py-0">
        <CardHeader className="border-b py-4">
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Intermediate Stops</CardTitle>
            <Button type="button" variant="outline" onClick={() => setStops((items) => [...items, emptyStop()])}>
              <Plus className="mr-2 h-4 w-4" />
              Add Stop
            </Button>
          </div>
        </CardHeader>
        <CardContent className="py-4">
          <p className="mb-3 text-sm text-muted-foreground">
            Optional preferred stops. Actual delivery stops come from selected orders.
          </p>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={stops.map((stop) => stop.localId)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {stops.map((stop, index) => (
                <SortableStop
                  key={stop.localId}
                  stop={stop}
                  index={index}
                  mappedRates={mappedRates}
                  unavailableRateIds={unavailableRateIds}
                  onChange={(patch) =>
                    setStops((items) =>
                      items.map((item) => (item.localId === stop.localId ? { ...item, ...patch } : item))
                    )
                  }
                  onRemove={() =>
                    setStops((items) =>
                      items.length === 1 ? [emptyStop()] : items.filter((item) => item.localId !== stop.localId)
                    )
                  }
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        </CardContent>
      </Card>

      <Card className="rounded-md py-0">
        <CardHeader className="border-b py-4">
          <CardTitle>Route Preview</CardTitle>
        </CardHeader>
        <CardContent className="py-4">
        <RouteMapPreview points={previewPoints} />
        </CardContent>
      </Card>

      <Card className="rounded-md py-0">
        <CardHeader className="border-b py-4">
          <CardTitle>Notes and Status</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 py-4 lg:grid-cols-2">
        <Field className="lg:col-span-2">
          <FieldLabel>Notes</FieldLabel>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional route note" />
        </Field>
        {mode === "edit" && (
          <div className="flex items-center gap-3">
            <Checkbox id="route-template-active" checked={isActive} onCheckedChange={(v) => setIsActive(v === true)} />
            <Label htmlFor="route-template-active">Active</Label>
          </div>
        )}
        </CardContent>
      </Card>
    </form>
  );
};

const RouteTemplatesTable = ({
  templates,
  onEdit,
}: {
  templates: RouteTemplateSummaryDto[];
  onEdit: (id: string) => void;
}) => {
  const deleteMutation = useDeleteRouteTemplate();
  const confirm = useConfirm();

  const handleDelete = (template: RouteTemplateSummaryDto) => {
    confirm({
      title: "Remove route template?",
      description: `${template.name} will be removed if it is not linked to existing trip snapshots.`,
      variant: "destructive",
      confirmLabel: "Remove",
      onConfirm: () =>
        withConfirmSuccess(deleteMutation.mutateAsync(template.id), `${template.name} removed`),
    });
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Route</TableHead>
            <TableHead>Destination</TableHead>
            <TableHead>Stops</TableHead>
            <TableHead>Estimate</TableHead>
            <TableHead>Policy</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {templates.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                No route templates found.
              </TableCell>
            </TableRow>
          )}
          {templates.map((template) => (
            <TableRow key={template.id}>
              <TableCell>
                <div className="space-y-1">
                  <p className="font-medium">{template.name}</p>
                  <p className="text-xs text-muted-foreground">{template.code}</p>
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  <p>{template.departureName}</p>
                  <p className="text-xs text-muted-foreground">{template.region || "No region"}</p>
                </div>
              </TableCell>
              <TableCell>{template.finalDestinationName || template.majorTown || "-"}</TableCell>
              <TableCell>{template.stopCount}</TableCell>
              <TableCell>
                {template.estimatedDistanceKm || template.estimatedDurationMinutes ? (
                  <span>
                    {template.estimatedDistanceKm ? `${template.estimatedDistanceKm} km` : ""}
                    {template.estimatedDistanceKm && template.estimatedDurationMinutes ? " · " : ""}
                    {template.estimatedDurationMinutes ? `${template.estimatedDurationMinutes} min` : ""}
                  </span>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell>
                <Badge variant="outline">{policyLabel(template.stopOrderPolicy)}</Badge>
              </TableCell>
              <TableCell>
                <Badge variant={template.isActive ? "default" : "secondary"}>
                  {template.isActive ? "Active" : "Inactive"}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(template.id)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(template)}>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const RouteTemplatesPage = () => {
  const [mode, setMode] = useState<PageMode>({ type: "list" });
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const { data, isLoading, error } = useRouteTemplateList({
    pageNumber,
    pageSize: 100,
    search: search.trim() || undefined,
  });
  const editId = mode.type === "edit" ? mode.id : null;
  const editQuery = useRouteTemplate(editId);

  if (mode.type === "create") {
    return (
      <main className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">New Route Template</h1>
          <p className="text-sm text-muted-foreground">Define departure, mapped stops, and final destination.</p>
        </div>
        <RouteTemplateForm
          mode="create"
          onCancel={() => setMode({ type: "list" })}
          onSaved={() => setMode({ type: "list" })}
        />
      </main>
    );
  }

  if (mode.type === "edit") {
    return (
      <main className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Edit Route Template</h1>
          <p className="text-sm text-muted-foreground">Adjust mapped stops without changing scheduled trip snapshots.</p>
        </div>
        {editQuery.isLoading && <LoadingAnimation />}
        {editQuery.error && <p className="text-red-600">Failed to load route template.</p>}
        {editQuery.data && (
          <RouteTemplateForm
            mode="edit"
            template={editQuery.data}
            onCancel={() => setMode({ type: "list" })}
            onSaved={() => setMode({ type: "list" })}
          />
        )}
      </main>
    );
  }

  return (
    <main className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Route Templates</h1>
          <p className="text-sm text-muted-foreground">Reusable dispatch routes for mapped delivery guidance.</p>
        </div>
        <Button onClick={() => setMode({ type: "create" })}>
          <Plus className="mr-2 h-4 w-4" />
          New Route
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPageNumber(1); }}
          placeholder="Search routes"
          className="pl-10"
        />
      </div>

      {isLoading && <LoadingAnimation />}
      {error && <p className="text-red-600">Failed to load route templates.</p>}
      {!isLoading && !error && (
        <>
          <RouteTemplatesTable templates={data?.items ?? []} onEdit={(id) => setMode({ type: "edit", id })} />
          {(data?.totalPages ?? 1) > 1 && (
            <div className="flex items-center justify-end gap-3 text-sm">
              <Button type="button" variant="outline" disabled={pageNumber <= 1} onClick={() => setPageNumber((page) => page - 1)}>Previous</Button>
              <span>Page {pageNumber} of {data?.totalPages}</span>
              <Button type="button" variant="outline" disabled={pageNumber >= (data?.totalPages ?? 1)} onClick={() => setPageNumber((page) => page + 1)}>Next</Button>
            </div>
          )}
        </>
      )}
    </main>
  );
};
